import { Link, Text } from '@fluentui/react';
import React from 'react';
import { createMachine } from 'xstate';
import { Model } from 'xstate/lib/model.types';
import { Input } from '../../components';
import {
  BaseContext,
  BaseEvent,
  customElement,
  remoteInput,
  remoteMessage,
  successMessage,
} from '../../machine';
import {
  EmailConsentReadOnly,
  NotEligibleInsurance,
  TextConsentReadOnly,
  ZoryvePatientAuthorizationReadOnly,
  ZoryveSavingsCard,
  zoryvePatientAuthorization,
  zoryvePatientAuthorizationCheckbox,
} from './ZoryveDirect.components';
import { sendEmail, sendText, updateContact } from './ZoryveDirect.services';

export const WELCOME = 'WELCOME';
export const WELCOME_THANK_YOU = 'WELCOME_THANK_YOU';
export const INSURANCE_PLAN = 'INSURANCE_PLAN';
export const INSURANCE_PLAN_ACK = 'INSURANCE_PLAN_ACK';
export const INSURANCE_PLAN_DECISION = 'INSURANCE_PLAN_DECISION';
export const NOT_ELIGIBLE_INSURANCE = 'NOT_ELIGIBLE_INSURANCE';
export const NOT_ELIGIBLE_TERMS = 'NOT_ELIGIBLE_TERMS';
export const SAVINGS_CARD = 'SAVINGS_CARD';
export const EMAIL_OR_TEXT_CARD = 'EMAIL_OR_TEXT_CARD';
export const EMAIL_OR_TEXT_CARD_ACK = 'EMAIL_OR_TEXT_CARD_ACK';
export const EMAIL_CARD_DECISION = 'EMAIL_OR_TEXT_CARD_DECISION';
export const EMAIL_MY_CARD = 'EMAIL_MY_CARD';
export const EMAIL_MY_CARD_MSG = 'EMAIL_MY_CARD_MSG';
export const EMAIL_MY_CARD_ACK = 'EMAIL_MY_CARD_ACK';
export const THANKS_EMAIL_CARD = 'THANKS_EMAIL_CARD';
export const TEXT_CARD_DECISION = 'TEXT_CARD_DECISION';
export const TEXT_MY_CARD = 'TEXT_MY_CARD';
export const TEXT_MY_CARD_MSG = 'TEXT_MY_CARD_MSG';
export const TEXT_MY_CARD_ACK = 'TEXT_MY_CARD_ACK';
export const THANKS_TEXT_CARD = 'THANKS_TEXT_CARD';
export const FINAL_BLANK_STATE = 'FINAL_BLANK_STATE';
export const UPDATE_EMAIL = 'UPDATE_EMAIL';
export const SEND_EMAIL = 'SEND_EMAIL';
export const SEND_TEXT = 'SEND_TEXT';
export const PATIENT_AUTHORIZATION = 'PATIENT_AUTHORIZATION';
export const PATIENT_AUTHORIZATION_ACK = 'PATIENT_AUTHORIZATION_ACK';
export const PATIENT_AUTHORIZATION_READ_ONLY = 'PATIENT_AUTHORIZATION_READ_ONLY';

export const REMOTE_MESSAGES = {
  WELCOME: (
    <Text>
      Please note that Arcutis has a privacy notice that you can view at{' '}
      <Link
        style={{ color: 'white' }}
        target="_blank"
        href="https://www.arcutis.com/privacy"
        rel="noreferrer"
      >
        <u>www.arcutis.com/privacy</u>
      </Link>
    </Text>
  ),
  WELCOME_THANK_YOU:
    'Thank you for your interest in enrolling in the ZORYVE Direct Savings Card Program! First, let\'s make sure that you\'re eligible for great savings.',
  NOT_ELIGIBLE_TERMS: (
    <Text>
      We’re sorry, you are not eligible for the ZORYVE Direct Savings Card Program. Please see
      program{' '}
      <Link
        style={{ color: 'white' }}
        target="_blank"
        href="https://www.zoryve.com/save-with-zoryve-direct?anchor=terms"
        rel="noreferrer"
      >
        <u>Terms & Conditions</u>
      </Link>{' '}
      for additional information. You can still sign up to receive updates on ZORYVE.
    </Text>
  ),
  INSURANCE_PLAN: 'What kind of insurance do you have?',
  SAVINGS_CARD: <ZoryveSavingsCard />,
  EMAIL_OR_TEXT_CARD: 'Please select any of the following to continue:',
  THANKS_EMAIL_CARD: 'We have emailed your savings card to the email address you provided.',
  THANKS_TEXT_CARD: 'We have sent your savings card to your phone number.',
  EMAIL_MY_CARD_MSG: <EmailConsentReadOnly />,
  TEXT_MY_CARD_MSG: <TextConsentReadOnly />,
  PATIENT_AUTHORIZATION_READ_ONLY: <ZoryvePatientAuthorizationReadOnly />,
  NOT_ELIGIBLE_INSURANCE: <NotEligibleInsurance />,
};

/**
 * ZORYVE Direct Program Flow V2.0
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const ZoryveDirectFlow = (model: Model<BaseContext, BaseEvent, void>, initial = WELCOME) =>
  createMachine<BaseContext, BaseEvent>(
    {
      id: 'ZoryveDirectFlow',
      initial: initial ?? WELCOME,
      context: model.initialContext,
      states: {
        // Welcome Message
        [WELCOME]: remoteMessage({
          id: WELCOME,
          message: REMOTE_MESSAGES.WELCOME,
          nextState: WELCOME_THANK_YOU,
        }),

        [WELCOME_THANK_YOU]: remoteMessage({
          id: WELCOME_THANK_YOU,
          message: REMOTE_MESSAGES.WELCOME_THANK_YOU,
          nextState: INSURANCE_PLAN,
        }),

        [INSURANCE_PLAN]: remoteInput(model, {
          id: INSURANCE_PLAN,
          message: REMOTE_MESSAGES.INSURANCE_PLAN,
          component: 'ButtonGroup',
          componentInputs: [
            {
              id: 1,
              key: INSURANCE_PLAN,
              name: 'Commercial or Private',
              value: 'Commercial or Private',
            },
            {
              id: 2,
              key: INSURANCE_PLAN,
              name: 'Government provided (Medicare, Medicaid, etc)',
              value: 'Government Provided',
            },
            {
              id: 3,
              key: INSURANCE_PLAN,
              name: 'None / Other',
              value: 'None / Other',
            },
          ],
          nextState: INSURANCE_PLAN_ACK,
        }),

        [INSURANCE_PLAN_ACK]: successMessage({
          id: INSURANCE_PLAN_ACK,
          parent: INSURANCE_PLAN,
          nextState: INSURANCE_PLAN_DECISION,
        }),

        [INSURANCE_PLAN_DECISION]: {
          id: INSURANCE_PLAN_DECISION,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          always: [
            {
              target: PATIENT_AUTHORIZATION,
              cond: 'userHasCommercialInsurance',
            },
            {
              target: NOT_ELIGIBLE_INSURANCE,
            },
          ],
        },

        [PATIENT_AUTHORIZATION]: remoteInput(model, {
          id: PATIENT_AUTHORIZATION,
          component: 'PatientAuthorization',
          componentInputs: [
            {
              id: 1,
              key: PATIENT_AUTHORIZATION,
              name: 'title',
              value: 'PATIENT AUTHORIZATION',
            },
            {
              id: 2,
              key: PATIENT_AUTHORIZATION,
              name: 'body',
              valueJSX: zoryvePatientAuthorization,
              value: '',
            },
            {
              id: 3,
              key: PATIENT_AUTHORIZATION,
              name: 'checkbox',
              valueJSX: zoryvePatientAuthorizationCheckbox,
              value: '',
            },
          ],
          nextState: PATIENT_AUTHORIZATION_READ_ONLY,
        }),

        [PATIENT_AUTHORIZATION_READ_ONLY]: customElement({
          id: PATIENT_AUTHORIZATION_READ_ONLY,
          jsx: REMOTE_MESSAGES.PATIENT_AUTHORIZATION_READ_ONLY,
          nextState: PATIENT_AUTHORIZATION_ACK,
        }),

        [PATIENT_AUTHORIZATION_ACK]: successMessage({
          id: PATIENT_AUTHORIZATION_ACK,
          parent: PATIENT_AUTHORIZATION,
          message: 'I Agree',
          nextState: SAVINGS_CARD,
        }),

        [SAVINGS_CARD]: customElement({
          id: SAVINGS_CARD,
          jsx: REMOTE_MESSAGES.SAVINGS_CARD,
          nextState: EMAIL_OR_TEXT_CARD,
        }),

        [EMAIL_OR_TEXT_CARD]: remoteInput(model, {
          id: EMAIL_OR_TEXT_CARD,
          message: REMOTE_MESSAGES.EMAIL_OR_TEXT_CARD,
          component: 'ButtonGroup',
          componentInputs: [
            {
              id: 1,
              key: EMAIL_OR_TEXT_CARD,
              name: 'Email my Savings Card to me',
              value: 'Email my Savings Card to me',
            },
            {
              id: 2,
              key: EMAIL_OR_TEXT_CARD,
              name: 'Text my Savings Card to me',
              value: 'Text my Savings Card to me',
            },
            {
              id: 3,
              key: EMAIL_OR_TEXT_CARD,
              name: 'Both',
              value: 'Both',
            },
          ],
          nextState: EMAIL_OR_TEXT_CARD_ACK,
        }),

        [EMAIL_OR_TEXT_CARD_ACK]: successMessage({
          id: EMAIL_OR_TEXT_CARD_ACK,
          message: 'Thank you for your input',
          parent: EMAIL_OR_TEXT_CARD,
          nextState: EMAIL_CARD_DECISION,
        }),

        [EMAIL_CARD_DECISION]: {
          id: EMAIL_CARD_DECISION,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          always: [
            {
              target: EMAIL_MY_CARD,
              cond: 'userOptedEmail',
            },
            {
              target: TEXT_MY_CARD,
            },
          ],
        },

        [EMAIL_MY_CARD]: remoteInput(model, {
          id: EMAIL_MY_CARD,
          component: 'InputEmailModal',
          nextState: EMAIL_MY_CARD_MSG,
        }),

        [EMAIL_MY_CARD_MSG]: customElement({
          id: EMAIL_MY_CARD_MSG,
          jsx: REMOTE_MESSAGES.EMAIL_MY_CARD_MSG,
          nextState: UPDATE_EMAIL,
        }),

        [UPDATE_EMAIL]: {
          id: UPDATE_EMAIL,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          invoke: {
            id: 'updateContact',
            src: async () => await updateContact(),
            onDone: [
              {
                target: EMAIL_MY_CARD_ACK,
                cond: 'userContactUpdated',
              },
            ],
          },
        },

        [EMAIL_MY_CARD_ACK]: successMessage({
          id: EMAIL_MY_CARD_ACK,
          message: 'Thank you for your input',
          parent: EMAIL_MY_CARD,
          nextState: SEND_EMAIL,
        }),

        [SEND_EMAIL]: {
          id: SEND_EMAIL,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          invoke: {
            id: 'sendEmail',
            src: async () => await sendEmail(),
            onDone: [
              {
                target: THANKS_EMAIL_CARD,
                cond: 'emailSent',
              },
            ],
          },
        },

        [THANKS_EMAIL_CARD]: remoteMessage({
          id: THANKS_EMAIL_CARD,
          message: REMOTE_MESSAGES.THANKS_EMAIL_CARD,
          nextState: TEXT_CARD_DECISION,
        }),

        [TEXT_CARD_DECISION]: {
          id: TEXT_CARD_DECISION,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          always: [
            {
              target: TEXT_MY_CARD,
              cond: 'userOptedText',
            },
            {
              target: FINAL_BLANK_STATE,
            },
          ],
        },

        [TEXT_MY_CARD]: remoteInput(model, {
          id: TEXT_MY_CARD,
          component: 'InputPhoneModal',
          nextState: TEXT_MY_CARD_MSG,
        }),

        [TEXT_MY_CARD_MSG]: customElement({
          id: TEXT_MY_CARD_MSG,
          jsx: REMOTE_MESSAGES.TEXT_MY_CARD_MSG,
          nextState: TEXT_MY_CARD_ACK,
        }),

        [TEXT_MY_CARD_ACK]: successMessage({
          id: TEXT_MY_CARD_ACK,
          message: 'Thank you for your input',
          parent: TEXT_MY_CARD,
          nextState: SEND_TEXT,
        }),

        [SEND_TEXT]: {
          id: SEND_TEXT,
          entry: model.assign({ loading: true }),
          exit: model.assign({ loading: false }),
          invoke: {
            id: 'sendText',
            src: async () => await sendText(),
            onDone: [
              {
                target: THANKS_TEXT_CARD,
                cond: 'textSent',
              },
            ],
          },
        },

        [THANKS_TEXT_CARD]: remoteMessage({
          id: THANKS_TEXT_CARD,
          message: REMOTE_MESSAGES.THANKS_TEXT_CARD,
        }),

        [NOT_ELIGIBLE_TERMS]: remoteMessage({
          id: NOT_ELIGIBLE_TERMS,
          message: REMOTE_MESSAGES.NOT_ELIGIBLE_TERMS,
        }),

        [NOT_ELIGIBLE_INSURANCE]: customElement({
          id: NOT_ELIGIBLE_INSURANCE,
          jsx: REMOTE_MESSAGES.NOT_ELIGIBLE_INSURANCE,
        }),

        [FINAL_BLANK_STATE]: customElement({
          id: FINAL_BLANK_STATE,
          jsx: <></>,
        }),
      },
    },
    {
      guards: {
        userHasCommercialInsurance: (ctx): boolean => {
          const input: Input = ctx?.responses?.[INSURANCE_PLAN].data.find(
            (item: Input) => item.key === INSURANCE_PLAN
          );
          return input && input.value.toLowerCase() === 'commercial or private';
        },

        userOptedEmail: (ctx): boolean => {
          const input: Input = ctx?.responses?.[EMAIL_OR_TEXT_CARD].data.find(
            (item: Input) => item.key === EMAIL_OR_TEXT_CARD
          );
          return (
            input &&
            (input.value.toLowerCase() === 'email my savings card to me' ||
              input.value.toLowerCase() === 'both')
          );
        },

        userOptedText: (ctx): boolean => {
          const input: Input = ctx?.responses?.[EMAIL_OR_TEXT_CARD].data.find(
            (item: Input) => item.key === EMAIL_OR_TEXT_CARD
          );
          return (
            input &&
            (input.value.toLowerCase() === 'text my savings card to me' ||
              input.value.toLowerCase() === 'both')
          );
        },

        userEnrolled: (ctx, event): boolean => {
          console.log('userEnrolled', event.data);
          return event.data ? true : false;
        },

        userContactUpdated: (ctx, event): boolean => {
          console.log('userContactUpdated', event.data);
          return event.data ? true : false;
        },

        emailSent: (ctx, event): boolean => {
          console.log('emailSent', event.data);
          return event.data ? true : false;
        },

        textSent: (ctx, event): boolean => {
          console.log('textSent', event.data);
          return event.data ? true : false;
        },
      },
    }
  );
